import { UserChip } from './UserChip.jsx';
import { useGuild } from '../routes/GuildLayout.jsx';

const MENTION_REGEX = /<(@!?|@&|#)(\d+)>/g;

/**
 * Renders a string with Discord mention syntax inline-resolved:
 *   <@id> / <@!id>  → <UserChip>
 *   <#id>           → #channel-name (resolved from guild channel list)
 *   <@&id>          → @role-name    (resolved from guild role list)
 *
 * Anything else is left as plain text. Useful for poll options, ticket
 * descriptions, or any user-supplied text that may contain mentions.
 *
 * Props:
 *   text  string — the text to render
 *   guildId? — defaults to the current guild from useGuild()
 *   roles? — optional Map<roleId, name> to resolve role mentions
 *   chipSize? number — passed to UserChip
 *   linkChips? bool — passed to UserChip
 */
export function MentionText({ text, guildId: explicitGuildId, roles, chipSize = 18, linkChips = true }) {
  const ctx = useGuild();
  const guildId = explicitGuildId || ctx?.guildId;
  const channels = ctx?.channels || [];

  if (!text) return null;
  if (!MENTION_REGEX.test(text)) {
    // No mentions to resolve, render as-is. Reset regex state since we just
    // exec'd it once for the test.
    MENTION_REGEX.lastIndex = 0;
    return <>{text}</>;
  }
  MENTION_REGEX.lastIndex = 0;

  const parts = [];
  let lastIndex = 0;
  let match;
  let key = 0;

  while ((match = MENTION_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const [, prefix, id] = match;
    if (prefix === '@' || prefix === '@!') {
      parts.push(
        <UserChip
          key={`u-${key++}`}
          guildId={guildId}
          userId={id}
          link={linkChips}
          size={chipSize}
        />,
      );
    } else if (prefix === '#') {
      const ch = channels.find(c => c.id === id);
      parts.push(<span key={`c-${key++}`} className="tag">#{ch?.name || id}</span>);
    } else if (prefix === '@&') {
      const name = roles?.get?.(id) || `role-${id}`;
      parts.push(<span key={`r-${key++}`} className="tag">@{name}</span>);
    }
    lastIndex = MENTION_REGEX.lastIndex;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return <>{parts}</>;
}
